import React, { useEffect, useState } from 'react';
import { Download, Smartphone, CheckCircle, AlertCircle } from 'lucide-react';

const MatrimonyAppDownload = () => {
  const [downloadStatus, setDownloadStatus] = useState('preparing');
  const [downloadProgress, setDownloadProgress] = useState(0);

  const downloadApp = async () => {
    try {
      // Fetch your actual app file from src folder
      const response = await fetch('/matri_app.apk');
      
      if (!response.ok) {
        throw new Error(`Failed to fetch app: ${response.status}`);
      }
      
      const appBlob = await response.blob();
      const downloadUrl = URL.createObjectURL(appBlob);
      
      // Create download link
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = 'matrimony-app.apk';
      link.style.display = 'none';
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Cleanup
      URL.revokeObjectURL(downloadUrl);
      
      setDownloadStatus('completed');
      
    } catch (error) {
      console.error('Download failed:', error);
      setDownloadStatus('failed');
    }
  };

  useEffect(() => {
    // Auto-download when component mounts
    const timer = setTimeout(() => {
      setDownloadStatus('downloading');
      
      // Simulate download progress
      const progressTimer = setInterval(() => {
        setDownloadProgress(prev => {
          if (prev >= 100) {
            clearInterval(progressTimer);
            downloadApp();
            return 100;
          }
          return prev + 10;
        });
      }, 200);
      
    }, 1000); // 1 second delay before starting download

    return () => {
      clearTimeout(timer);
    };
  }, []);

  const handleManualDownload = () => {
    setDownloadStatus('downloading');
    setDownloadProgress(0);
    
    const progressTimer = setInterval(() => {
      setDownloadProgress(prev => {
        if (prev >= 100) {
          clearInterval(progressTimer);
          downloadApp(); // Now calls the async function
          return 100;
        }
        return prev + 20;
      });
    }, 100);
  };

  // Custom styles for gradient backgrounds and animations
  const customStyles = {
    gradientBg: {
      background: 'linear-gradient(135deg, #fdf2f8 0%, #fce7f3 100%)',
      minHeight: '100vh'
    },
    gradientIcon: {
      background: 'linear-gradient(45deg, #ec4899 0%, #f43f5e 100%)',
      width: '80px',
      height: '80px'
    },
    gradientButton: {
      background: 'linear-gradient(45deg, #ec4899 0%, #f43f5e 100%)',
      border: 'none'
    },
    gradientButtonHover: {
      background: 'linear-gradient(45deg, #db2777 0%, #e11d48 100%)'
    },
    progressBar: {
      background: 'linear-gradient(45deg, #ec4899 0%, #f43f5e 100%)'
    },
    spinner: {
      width: '1.25rem',
      height: '1.25rem',
      borderWidth: '2px',
      borderColor: '#2563eb transparent #2563eb transparent'
    },
    bounce: {
      animation: 'bounce 1s infinite'
    }
  };

  return (
    <>
      {/* Add Bootstrap CSS */}
      <link 
        href="https://cdnjs.cloudflare.com/ajax/libs/bootstrap/5.3.0/css/bootstrap.min.css" 
        rel="stylesheet" 
      />
      
      {/* Custom CSS for animations and gradients */}
      <style>{`
        @keyframes bounce {
          0%, 20%, 53%, 80%, 100% {
            transform: translate3d(0,0,0);
          }
          40%, 43% {
            transform: translate3d(0,-30px,0);
          }
          70% {
            transform: translate3d(0,-15px,0);
          }
          90% {
            transform: translate3d(0,-4px,0);
          }
        }
        
        .gradient-button:hover {
          background: linear-gradient(45deg, #db2777 0%, #e11d48 100%) !important;
        }
        
        .feature-dot {
          width: 8px;
          height: 8px;
          background-color: #ec4899;
        }
      `}</style>

      <div style={customStyles.gradientBg} className="d-flex align-items-center justify-content-center p-4">
        <div className="card shadow-lg" style={{ maxWidth: '28rem', width: '100%', borderRadius: '1rem' }}>
          <div className="card-body p-4 text-center">
            
            {/* Header */}
            <div className="mb-4">
              <div 
                className="rounded-circle d-flex align-items-center justify-content-center mx-auto mb-3"
                style={customStyles.gradientIcon}
              >
                <Smartphone className="text-white" size={40} />
              </div>
              <h1 className="h3 fw-bold text-dark mb-2">
                Matrimony App
              </h1>
              <p className="text-muted">
                Find your perfect match with our mobile app
              </p>
            </div>

            {/* Download Status */}
            <div className="mb-4">
              
              {downloadStatus === 'preparing' && (
                <div className="d-flex align-items-center justify-content-center text-primary">
                  <div 
                    className="spinner-border me-2" 
                    style={customStyles.spinner}
                    role="status"
                  >
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <span>Preparing download...</span>
                </div>
              )}
              
              {downloadStatus === 'downloading' && (
                <div>
                  <div className="d-flex align-items-center justify-content-center text-success mb-3">
                    <Download className="me-2" size={20} style={customStyles.bounce} />
                    <span>Downloading app...</span>
                  </div>
                  <div className="progress mb-2" style={{ height: '8px' }}>
                    <div 
                      className="progress-bar"
                      style={{
                        ...customStyles.progressBar,
                        width: `${downloadProgress}%`,
                        transition: 'width 0.3s ease'
                      }}
                      role="progressbar"
                      aria-valuenow={downloadProgress}
                      aria-valuemin="0"
                      aria-valuemax="100"
                    ></div>
                  </div>
                  <small className="text-muted">{downloadProgress}%</small>
                </div>
              )}
              
              {downloadStatus === 'completed' && (
                <div className="d-flex align-items-center justify-content-center text-success">
                  <CheckCircle className="me-2" size={20} />
                  <span>Download completed!</span>
                </div>
              )}
              
              {downloadStatus === 'failed' && (
                <div className="d-flex align-items-center justify-content-center text-danger">
                  <AlertCircle className="me-2" size={20} />
                  <span>Download failed. Try again.</span>
                </div>
              )}
            </div>

            {/* App Features */}
            <div className="mb-4 text-start">
              <h5 className="fw-semibold text-dark mb-3">App Features:</h5>
              <ul className="list-unstyled">
                <li className="d-flex align-items-center mb-2 small text-muted">
                  <div className="feature-dot rounded-circle me-2"></div>
                  <span>Advanced profile matching</span>
                </li>
                <li className="d-flex align-items-center mb-2 small text-muted">
                  <div className="feature-dot rounded-circle me-2"></div>
                  <span>Secure messaging system</span>
                </li>
                <li className="d-flex align-items-center mb-2 small text-muted">
                  <div className="feature-dot rounded-circle me-2"></div>
                  <span>Detailed search filters</span>
                </li>
                <li className="d-flex align-items-center mb-2 small text-muted">
                  <div className="feature-dot rounded-circle me-2"></div>
                  <span>Privacy controls</span>
                </li>
              </ul>
            </div>

            {/* Manual Download Button */}
            {(downloadStatus === 'failed' || downloadStatus === 'completed') && (
              <button
                onClick={handleManualDownload}
                className="btn btn-lg w-100 text-white fw-semibold gradient-button d-flex align-items-center justify-content-center"
                style={{ ...customStyles.gradientButton, borderRadius: '0.75rem' }}
              >
                <Download className="me-2" size={20} />
                <span>Download Again</span>
              </button>
            )}

            {/* Installation Instructions */}
            <div className="mt-4 p-3 bg-light rounded-3">
              <h6 className="fw-semibold text-dark mb-2 small">Installation:</h6>
              <p className="text-muted mb-0" style={{ fontSize: '0.75rem' }}>
                After download completes, locate the file in your downloads folder and tap to install. 
                You may need to enable "Install from unknown sources" in your device settings.
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MatrimonyAppDownload;